/**
 * Менеджер завантажень диска (`DISK-001` §2.1, §4.5, наряд Д1.10): типи
 * протоколу шматків і стану черги.
 */

/** Що робити з зайнятою назвою — `UploadConflict` беку. */
export type DiskUploadConflict = 'keep_both' | 'version' | 'fail'

/** Відповідь користувача в діалозі конфлікту назв. */
export type DiskConflictChoice = 'version' | 'keep_both' | 'skip'

/** Звідки прийшли файли: окремі файли питають про конфлікт, папка — «Зберегти обидва». */
export type DiskUploadSource = 'files' | 'folder'

/**
 * Стан файлу в черзі.
 *
 * - `conflict` — назва зайнята, чекає на відповідь у діалозі;
 * - `needsFile` — сесія пережила перезавантаження сторінки, але браузер не
 *   дає прочитати файл знову без вибору користувача.
 */
export type DiskUploadState =
    | 'queued'
    | 'uploading'
    | 'paused'
    | 'done'
    | 'error'
    | 'cancelled'
    | 'conflict'
    | 'skipped'
    | 'needsFile'

/** Сесія шматків — `UploadSessionView` беку. */
export interface DiskUploadSession {
    uuid: string
    /** `active` | `completed` | `cancelled` | `expired` */
    status: string
    size: number
    chunkSize: number
    chunkCount: number
    received: number[]
    missing: number[]
    expiresAt: string | null
}

/** Файл, який обрали, перетягнули чи вставили, разом із місцем у вкладеній папці. */
export interface DiskPickedFile {
    file: File
    /** Папки від цілі до файлу через `/`; порожньо — файл кладеться просто в ціль. */
    relativePath: string
}

/** Куди вантажимо: папка й підписи для панелі. */
export interface DiskUploadTarget {
    parent: string
    parentName: string
    storageUuid: string | null
}

/**
 * Запис незавершеної сесії в IndexedDB: його досить, щоб після
 * перезавантаження сторінки звірити повторно вибраний файл і докачати
 * відсутні шматки.
 */
export interface DiskUploadRecord {
    uploadUuid: string
    /** Чия сесія: на спільному комп'ютері чужі записи не показуємо. */
    ownerKey: string
    parent: string
    parentName: string
    storageUuid: string | null
    name: string
    size: number
    lastModified: number
    relativePath: string
    conflict: DiskUploadConflict
    createdAt: number
}

/** Скільки файлів вантажиться одночасно. */
export const DISK_UPLOAD_PARALLEL_FILES = 2

/** Скільки шматків одного файлу летить одночасно. */
export const DISK_UPLOAD_PARALLEL_CHUNKS = 2

/**
 * До якого розміру рахуємо sha256 у браузері. Web Crypto не вміє рахувати
 * потоком — файл цілком читається в пам'ять, тож більші файли йдуть без
 * контрольної суми (бек це дозволяє й рахує її сам при склеюванні).
 */
export const DISK_UPLOAD_HASH_MAX_BYTES = 200 * 1024 * 1024

/** Скільки разів повторити шматок після обриву зв'язку чи 5xx. */
export const DISK_UPLOAD_CHUNK_RETRIES = 3
